/**
 * UPT-2124 J4b (spec `web/SPEC-UPT-2124-J4.md` §2 n° 2, 4, 7) — les outils qui REMPLISSENT : `ui_fill` (texte, nombre, date, case, liste), `ui_select`
 * (liste native ou ARIA, groupe de boutons radio), `ui_undo` (pile de 10 remplissages PAR PAGE) ; et la CONFIRMATION d'un clic qui écrit (carte J2d,
 * « oui » vocal ou clic) avec la garde « formulaire modifié par Géode ».
 *
 * - Un remplissage n'enregistre jamais rien : il note le formulaire (ou le dialogue) comme MODIFIÉ PAR GÉODE ; ensuite tout bouton de ce formulaire, hors
 *   Suivant / Fermer / Annuler…, demande la confirmation, la carte listant les NOMS des champs touchés (jamais les valeurs).
 * - Champ `password`, `data-sensitive`, valeur masquée, nom qui parle d'un secret, lecture seule, désactivé : refusés.
 * - Une valeur que le champ ne retient pas (date illisible, nombre) est refusée, rien ne bouge.
 */
import { requestVoiceConfirmation, type VoiceConfirmDecision } from '../voiceConfirm';
import { useVoiceStore } from '../voiceStore';
import type { VoiceRegisteredTool, VoiceToolOutput } from '../voiceToolRegistry';
import {
  FILLABLE_ROLES,
  clickElement,
  collectCandidates,
  fillElement,
  formContainerOf,
  isDisabled,
  isReadOnly,
  isSensitive,
  matchOption,
  optionsOf,
  resolveByName,
  resolveCandidate,
  restoreFieldValue,
  scopeRoots,
  selectNativeOption,
  valueToText,
  type UiCandidate,
  type UiOption,
} from './uiActor';
import { playUiGhost, showUiHighlight } from './uiGhostStore';
import { defaultSleep, json, pageKey, type UiToolKit, type UiWriteTools } from './uiToolKit';
import { UI_TOOL_DEFINITIONS, type UiToolName } from './uiToolDefs';
import { normalizeUiText } from './uiRiskTable';

/** Taille de la pile d'annulation (par page). */
export const UI_UNDO_DEPTH = 10;

/** Un champ dont le NOM parle d'un secret n'est jamais rempli par la voix (mot de passe, jeton, clé d'API). */
const SECRET_FIELD_NAME = /secret|token|jeton|password|mot de passe|passwort|cle api|api key|schlussel|chiave|مفتاح|كلمة السر/;

interface UndoEntry {
  element: HTMLElement;
  previous: string | boolean;
  name: string;
}

export const createUiWriteTools = (kit: UiToolKit): UiWriteTools => {
  const { env, doc, t, journal, notFound, ambiguous, exclusive, cancelledOutput, performClick } = kit;
  let undoPath = '';
  let undoStack: UndoEntry[] = [];
  /** Formulaires / dialogues modifiés par Géode → les noms des champs touchés (jamais les valeurs). */
  const touched = new WeakMap<Element, string[]>();

  const isTouched = (element: HTMLElement): boolean => {
    const form = (element as HTMLButtonElement).form;
    if (form && touched.has(form)) return true;
    let node: Element | null = element;
    while (node) {
      if (touched.has(node)) return true;
      node = node.parentElement;
    }
    return false;
  };

  const markTouched = (element: HTMLElement, name: string): void => {
    const container = formContainerOf(element);
    const names = touched.get(container) ?? [];
    if (!names.includes(name)) names.push(name);
    touched.set(container, names);
  };

  const pushUndo = (entry: UndoEntry): void => {
    const path = pageKey(env.pathname());
    if (path !== undoPath) {
      undoPath = path;
      undoStack = [];
    }
    undoStack.push(entry);
    if (undoStack.length > UI_UNDO_DEPTH) undoStack.shift();
  };

  // ------------------------------------------------------------------------------------------------------------ confirmation d'un clic qui écrit
  const askToConfirmClick = (candidate: UiCandidate, touchedForm: boolean): VoiceToolOutput => {
    const { element, name } = candidate;
    const changed = touched.get(formContainerOf(element)) ?? [];
    const question = t(touchedForm && changed.length > 0 ? 'voice.ui.confirm.questionTouched' : 'voice.ui.confirm.question', { name });
    const fields = [{ label: t('voice.ui.confirm.field.action'), value: t('voice.ui.confirm.field.actionValue', { name }) }];
    if (changed.length > 0) {
      fields.push({ label: t('voice.ui.confirm.field.changed'), value: changed.join(', ') });
    }
    let cardId = '';
    let clearHighlight: () => void = () => undefined;
    const closeCard = (patch: 'done' | 'expired' | 'removed', title?: string): void => {
      const store = useVoiceStore.getState();
      if (patch === 'removed') {
        store.removeCard(cardId);
        return;
      }
      store.updateCard(cardId, {
        title: title ?? t('voice.ui.confirm.title'),
        confirm: { confirmationId: confirmation.id, status: patch, fields, question },
      });
    };
    const confirmation = requestVoiceConfirmation({
      tool: 'ui_click',
      onDecision: async (decision: VoiceConfirmDecision) => {
        clearHighlight();
        if (decision === 'no') {
          closeCard('removed');
          journal('ui_click', 'rejected', 'confirm', name);
          return json({ cancelled: true, answer: t('voice.confirm.cancelled') });
        }
        const result = await exclusive(async () => performClick('ui_click', candidate, 'confirm'));
        closeCard((JSON.parse(result.outputJson) as { done?: boolean }).done === true ? 'done' : 'removed', t('voice.ui.confirm.doneTitle'));
        return result;
      },
      onExpire: () => {
        clearHighlight();
        closeCard('expired');
        journal('ui_click', 'stale', 'confirm', name);
      },
    });
    cardId = useVoiceStore.getState().addCard({
      title: t('voice.ui.confirm.title'),
      bodyMd: question,
      kind: 'confirm',
      confirm: { confirmationId: confirmation.id, status: 'pending', fields, question, confirmLabel: t('voice.ui.confirm.yes') },
    });
    clearHighlight = showUiHighlight({ label: t('voice.ui.ghost.confirmPending', { name }), element, ms: 0 });
    journal('ui_click', 'proposed', 'confirm', name);
    return json({ confirmation: 'pending', answer: question });
  };

  // ------------------------------------------------------------------------------------------------------------ ui_select / ui_fill
  const fieldError = (error: string, name: string, value: string): VoiceToolOutput => {
    const key =
      error === 'read_only'
        ? 'voice.ui.answer.readOnly'
        : error === 'disabled'
          ? 'voice.ui.answer.disabled'
          : error === 'sensitive'
            ? 'voice.ui.answer.sensitive'
            : error === 'invalid_value'
              ? 'voice.ui.answer.invalidValue'
              : 'voice.ui.answer.unsupported';
    return json({ error, answer: t(key, { name, value }) });
  };

  const chooseInList = async (candidate: UiCandidate, optionName: string): Promise<VoiceToolOutput> => {
    const { element, name } = candidate;
    let options: UiOption[] = optionsOf(element);
    let opener = false;
    if (options.length === 0 && candidate.role === 'combobox' && element.tagName.toLowerCase() !== 'select') {
      // Liste ARIA : ouverte par un clic, ses options vivent dans un `listbox` (souvent un portail).
      opener = true;
      clickElement(element);
      await (env.sleep ?? defaultSleep)(80);
      const controls = element.getAttribute('aria-controls');
      const listbox = (controls ? doc.getElementById(controls) : null) ?? doc.querySelector<HTMLElement>('[role="listbox"]');
      options = listbox ? optionsOf(listbox) : [];
    }
    const match = matchOption(options, optionName);
    if (match.kind === 'none') {
      if (opener) clickElement(element);
      const list = options.slice(0, 8).map((option) => option.label).join(', ');
      return json({ error: 'option_not_found', answer: t('voice.ui.answer.noOption', { name, option: optionName, options: list }) });
    }
    if (match.kind === 'ambiguous') {
      if (opener) clickElement(element);
      const choices = match.options.slice(0, 5).map((option) => option.label).join(` ${t('voice.ui.or')} `);
      return json({ error: 'ambiguous', answer: t('voice.ui.answer.ambiguousOption', { name, option: optionName, choices }) });
    }
    const option = match.option;
    const shown = (option.element && option.element.tagName !== 'OPTION' ? option.element : element) as HTMLElement;
    const outcome = await playUiGhost({ label: t('voice.ui.ghost.select', { name, option: option.label }), element: shown, ms: env.ghostMs });
    if (outcome === 'cancelled') {
      if (opener) clickElement(element);
      return cancelledOutput('ui_select', 'free', name);
    }
    if (element.tagName.toLowerCase() === 'select') {
      const { previous } = selectNativeOption(element as HTMLSelectElement, option);
      pushUndo({ element, previous, name });
      markTouched(element, name);
    } else if (option.element) {
      clickElement(option.element);
      markTouched(element, name);
    }
    journal('ui_select', 'applied', 'free', name);
    return json({ done: true, answer: t('voice.ui.answer.selected', { name, option: option.label }) });
  };

  const uiSelect = (args: Record<string, unknown>): Promise<VoiceToolOutput> =>
    exclusive(async () => {
      const label = typeof args.label === 'string' ? args.label.trim() : '';
      const optionName = typeof args.option === 'string' ? args.option.trim() : '';
      if (!label || !optionName) return notFound(label);
      const roots = scopeRoots(doc);
      const candidates = collectCandidates(doc, roots).filter(
        (candidate) =>
          (candidate.role === 'combobox' || candidate.role === 'listbox' || candidate.role === 'radiogroup' || candidate.role === 'group') &&
          (candidate.role !== 'group' || candidate.element.querySelector('input[type="radio"], [role="radio"]') !== null)
      );
      const resolution = resolveCandidate(candidates, label);
      if (resolution.kind === 'none') {
        journal('ui_select', 'invalid', 'free', label);
        return notFound(label);
      }
      if (resolution.kind === 'ambiguous') return ambiguous(label, resolution.candidates);
      if (isSensitive(resolution.candidate.element)) return fieldError('sensitive', resolution.candidate.name, '');
      if (isDisabled(resolution.candidate.element)) return fieldError('disabled', resolution.candidate.name, '');
      return chooseInList(resolution.candidate, optionName);
    });

  const uiFill = (args: Record<string, unknown>): Promise<VoiceToolOutput> =>
    exclusive(async () => {
      const label = typeof args.label === 'string' ? args.label.trim() : '';
      const rawValue = args.value;
      if (!label) return notFound('');
      const resolution = resolveByName(doc, label, FILLABLE_ROLES);
      if (resolution.kind === 'none') {
        journal('ui_fill', 'invalid', 'free', label);
        return notFound(label);
      }
      if (resolution.kind === 'ambiguous') return ambiguous(label, resolution.candidates);
      const candidate = resolution.candidate;
      const { element, name } = candidate;
      const value = valueToText(rawValue);
      if (isSensitive(element) || SECRET_FIELD_NAME.test(normalizeUiText(name))) {
        journal('ui_fill', 'rejected', 'forbidden', name);
        return fieldError('sensitive', name, '');
      }
      if (isDisabled(element)) return fieldError('disabled', name, value);
      if (isReadOnly(element)) return fieldError('read_only', name, value);
      if (candidate.role === 'combobox' || candidate.role === 'listbox' || candidate.role === 'radiogroup') {
        return chooseInList(candidate, value);
      }
      if (candidate.role === 'radio') {
        return performClick('ui_fill', candidate, 'free');
      }
      const outcome = await playUiGhost({ label: t('voice.ui.ghost.fill', { name }), element, ms: env.ghostMs });
      if (outcome === 'cancelled') return cancelledOutput('ui_fill', 'free', name);
      const result = fillElement(element, rawValue);
      if (result.ok === false) {
        journal('ui_fill', 'invalid', 'free', name);
        return fieldError(result.error, name, value);
      }
      pushUndo({ element, previous: result.previous, name });
      markTouched(element, name);
      journal('ui_fill', 'applied', 'free', name);
      const shown = result.kind === 'check' ? t(result.shown === 'true' ? 'voice.ui.checked' : 'voice.ui.unchecked') : result.shown;
      return json({ done: true, answer: t('voice.ui.answer.filled', { name, value: shown }) });
    });

  // ------------------------------------------------------------------------------------------------------------ ui_undo
  const uiUndo = (): Promise<VoiceToolOutput> =>
    exclusive(async () => {
      const path = pageKey(env.pathname());
      if (path !== undoPath) {
        undoPath = path;
        undoStack = [];
      }
      const entry = undoStack.pop();
      if (!entry) {
        return json({ error: 'nothing_to_undo', answer: t('voice.ui.answer.nothingToUndo') });
      }
      if (!entry.element.isConnected) {
        return json({ error: 'gone', answer: t('voice.ui.answer.undoGone', { name: entry.name }) });
      }
      const outcome = await playUiGhost({ label: t('voice.ui.ghost.undo', { name: entry.name }), element: entry.element, ms: env.ghostMs });
      if (outcome === 'cancelled') {
        undoStack.push(entry);
        return cancelledOutput('ui_undo', 'free', entry.name);
      }
      restoreFieldValue(entry.element, entry.previous);
      journal('ui_undo', 'applied', 'free', entry.name);
      return json({ done: true, answer: t('voice.ui.answer.undone', { name: entry.name }) });
    });

  const tool = (name: UiToolName, execute: VoiceRegisteredTool['execute']): VoiceRegisteredTool => ({ definition: UI_TOOL_DEFINITIONS[name], execute });
  return {
    tools: [tool('ui_fill', (args) => uiFill(args)), tool('ui_select', (args) => uiSelect(args)), tool('ui_undo', () => uiUndo())],
    isTouched,
    askToConfirmClick,
  };
};
